'use client';
import { useEffect, useState, useMemo } from 'react';
 import Header from'@/components/common/Header';
 import Footer from'@/components/common/Footer';
 import Icon from'@/components/ui/AppIcon';

const indexEntries = [
  { id: 'entry_abap', term: 'ABAP', category: 'Technology', description: 'Advanced Business Application Programming language', section: 'Technical Playbook' },
  { id: 'entry_api', term: 'API', category: 'Technology', description: 'Application Programming Interface', section: 'Technical Playbook' },
  { id: 'entry_badi', term: 'BAdI', category: 'Pattern', description: 'Business Add-In for custom logic', section: 'Technical Playbook' },
  { id: 'entry_bluefield', term: 'Bluefield', category: 'Migration', description: 'Selective data transition approach', section: 'Migration Paths' },
  { id: 'entry_brownfield', term: 'Brownfield', category: 'Migration', description: 'System conversion approach', section: 'Migration Paths' },
  { id: 'entry_btp', term: 'BTP', category: 'Technology', description: 'Business Technology Platform', section: 'Technical Playbook' },
  { id: 'entry_cds', term: 'CDS', category: 'Technology', description: 'Core Data Services', section: 'Technical Playbook' },
  { id: 'entry_co', term: 'Controlling (CO)', category: 'Module', description: 'Cost and profitability analysis', section: 'Functional Playbook' },
  { id: 'entry_fiori', term: 'Fiori', category: 'Technology', description: 'User experience framework', section: 'Technical Playbook' },
  { id: 'entry_fi', term: 'Finance (FI)', category: 'Module', description: 'Financial accounting', section: 'Functional Playbook' },
  { id: 'entry_greenfield', term: 'Greenfield', category: 'Migration', description: 'New implementation approach', section: 'Migration Paths' },
  { id: 'entry_hana', term: 'HANA', category: 'Technology', description: 'In-memory database platform', section: 'Technical Playbook' },
  { id: 'entry_idoc', term: 'IDoc', category: 'Technology', description: 'Intermediate Document for data transfer', section: 'Landscape Map' },
  { id: 'entry_mm', term: 'Materials Management (MM)', category: 'Module', description: 'Procurement and inventory', section: 'Functional Playbook' },
  { id: 'entry_odata', term: 'OData', category: 'Technology', description: 'Open Data Protocol', section: 'Technical Playbook' },
  { id: 'entry_pp', term: 'Production Planning (PP)', category: 'Module', description: 'Manufacturing planning', section: 'Functional Playbook' },
  { id: 'entry_qm', term: 'Quality Management (QM)', category: 'Module', description: 'Quality assurance processes', section: 'Functional Playbook' },
  { id: 'entry_rfc', term: 'RFC', category: 'Technology', description: 'Remote Function Call', section: 'Landscape Map' },
  { id: 'entry_sd', term: 'Sales & Distribution (SD)', category: 'Module', description: 'Order-to-cash processes', section: 'Functional Playbook' },
  { id: 'entry_ui5', term: 'UI5', category: 'Technology', description: 'User interface framework', section: 'Technical Playbook' },
  { id: 'entry_journal', term: 'Universal Journal', category: 'Process', description: 'Unified financial data structure', section: 'Functional Playbook' },
]

const categories = ['All', 'Module', 'Technology', 'Migration', 'Pattern', 'Process']

export default function AZIndexPage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('All')

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal')
    if (!reveals.length) return

    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    )
    reveals.forEach(el => observer.observe(el))

    return () => observer.disconnect()
  }, [])

  const filteredEntries = useMemo(() => {
    return indexEntries.filter((entry) => {
      const matchesSearch = entry.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           entry.description.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesCategory = selectedCategory === 'All' || entry.category === selectedCategory
      return matchesSearch && matchesCategory
    })
  }, [searchTerm, selectedCategory])

  const groupedEntries = useMemo(() => {
    const groups: Record<string, typeof indexEntries> = {}
    filteredEntries.forEach((entry) => {
      const letter = entry.term[0].toUpperCase()
      if (!groups[letter]) {
        groups[letter] = []
      }
      groups[letter].push(entry)
    })
    return groups
  }, [filteredEntries])

  const letters = Object.keys(groupedEntries).sort()

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />

      <main className="pt-24">
        {/* Page Header */}
        <section className="mx-auto max-w-7xl px-6 py-16 border-b border-border">
          <div className="reveal">
            <h1 className="font-serif text-display-md md:text-display-lg font-bold mb-4">
              A-Z Index
            </h1>
            <p className="font-mono text-lg text-muted-foreground max-w-3xl">
              Complete reference index of S/4HANA concepts, modules, and technologies. Search or browse alphabetically.
            </p>
          </div>
        </section>

        {/* Search & Filters */}
        <section className="sticky top-20 z-40 bg-background/95 backdrop-blur border-b border-border">
          <div className="mx-auto max-w-7xl px-6 py-6">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
              {/* Search */}
              <div className="relative flex-1">
                <Icon name="MagnifyingGlassIcon" size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" variant="outline" />
                <input
                  type="text"
                  placeholder="Search concepts, modules, technologies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-card border border-border rounded font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Category Filters */}
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 font-mono text-sm rounded transition-colors ${
                      selectedCategory === category
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-card border border-border hover:bg-muted'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Results Count */}
            <div className="mt-4">
              <p className="font-mono text-sm text-muted-foreground">
                {filteredEntries.length} {filteredEntries.length === 1 ? 'entry' : 'entries'} found
              </p>
            </div>
          </div>
        </section>

        {/* Alphabetical Index */}
        <section className="mx-auto max-w-7xl px-6 py-16">
          {letters.length === 0 ? (
            <div className="text-center py-20 reveal">
              <Icon name="MagnifyingGlassIcon" size={48} className="mx-auto mb-4 text-muted-foreground" variant="outline" />
              <p className="font-mono text-lg text-muted-foreground">No entries found matching your search</p>
            </div>
          ) : (
            <div className="space-y-12">
              {letters.map((letter) => (
                <div key={letter} id={letter} className="reveal">
                  <h2 className="font-serif text-4xl font-bold mb-6 pb-3 border-b-2 border-primary">
                    {letter}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {groupedEntries[letter].map((entry) => (
                      <div key={entry.id} className="p-5 border border-border rounded hover:bg-muted transition-colors">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <h3 className="font-mono font-semibold text-lg">{entry.term}</h3>
                          <span className="px-2 py-0.5 bg-primary/10 text-primary rounded font-mono text-xs shrink-0">
                            {entry.category}
                          </span>
                        </div>
                        <p className="font-mono text-sm text-muted-foreground mb-3">{entry.description}</p>
                        <a
                          href={`/${entry.section.toLowerCase().replace(/\s+/g, '-')}`}
                          className="inline-flex items-center gap-1 font-mono text-xs text-primary hover:underline"
                        >
                          View in {entry.section}
                          <Icon name="ArrowRightIcon" size={12} variant="outline" />
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Letter Navigation (Quick Jump) */}
        {letters.length > 0 && (
          <section className="mx-auto max-w-7xl px-6 py-8 border-t border-border">
            <div className="flex flex-wrap justify-center gap-2 reveal">
              <p className="font-mono text-sm text-muted-foreground w-full text-center mb-4">Quick Jump</p>
              {letters.map((letter) => (
                <a
                  key={letter}
                  href={`#${letter}`}
                  className="w-10 h-10 flex items-center justify-center border border-border rounded hover:bg-primary hover:text-primary-foreground font-mono font-semibold transition-colors"
                >
                  {letter}
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Navigation CTA */}
        <section className="mx-auto max-w-7xl px-6 py-16 border-t border-border">
          <div className="text-center space-y-6 reveal">
            <p className="font-mono text-muted-foreground">
              Return to main navigation
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/s-4-landscape-map"
                className="px-6 py-3 bg-primary text-primary-foreground font-mono text-sm rounded hover:opacity-90 transition-opacity"
              >
                View Landscape Map
              </a>
              <a
                href="/homepage"
                className="px-6 py-3 border border-border font-mono text-sm rounded hover:bg-muted transition-colors"
              >
                Back to Home
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}